import type { ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { BoxHeader } from "@/components/design/BoxHeader";

export function ServiceColumn({
  title,
  desc,
  children,
  footer,
}: {
  title: string;
  desc: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="box flex h-full flex-col bg-surface p-6">
      <BoxHeader title={title} variant="hero" />
      <div className="field-meta leading-snug text-ink/80">{desc}</div>
      <div className="mt-5 grid auto-rows-fr grid-cols-2 gap-3">{children}</div>
      {footer}
    </div>
  );
}

export function ServiceCard({
  icon,
  title,
  desc,
  tags,
  accent = false,
  comingSoon = false,
  onClick,
  disabled = false,
}: {
  icon: string;
  title: string;
  desc: string;
  tags: string;
  accent?: boolean;
  // Backend not migrated yet - shows a "Bald verfügbar" badge above the desc.
  comingSoon?: boolean;
  onClick?: () => void;
  disabled?: boolean;
}) {
  const interactive = Boolean(onClick);
  if (interactive) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="box-button flex h-full min-h-[160px] w-full flex-col p-4 text-left transition hover:-translate-y-0.5 disabled:opacity-50"
      >
        <ServiceCardBody icon={icon} title={title} desc={desc} tags={tags} accent={accent} comingSoon={comingSoon} />
      </button>
    );
  }
  return (
    <div className="box-button flex h-full min-h-[160px] flex-col p-4 text-left">
      <ServiceCardBody icon={icon} title={title} desc={desc} tags={tags} accent={accent} comingSoon={comingSoon} />
    </div>
  );
}

function ServiceCardBody({
  icon,
  title,
  desc,
  tags,
  accent,
  comingSoon,
}: {
  icon: string;
  title: string;
  desc: string;
  tags: string;
  accent: boolean;
  comingSoon: boolean;
}) {
  return (
    <>
      <div
        className={`flex h-7 w-7 items-center justify-center rounded-md text-base ${
          accent ? "bg-accent/15 text-accent" : "bg-ink/[0.06] text-ink/80"
        }`}
      >
        {icon}
      </div>
      <div className="field-value mt-3 font-medium">{title}</div>
      {comingSoon ? (
        // Same badge as NoBackend's comingSoon marker, inline above the desc.
        <span className="mt-1.5 inline-flex w-fit items-center rounded-full bg-accent/10 px-2 py-0.5 text-[8px] font-semibold tracking-[0.18em] text-accent">
          <FormattedMessage id="common.comingSoon" defaultMessage="Coming soon" />
        </span>
      ) : null}
      {/* Coming-soon cards blur their copy (like NoBackend's skeleton) so the
          feature clearly reads as not-yet-usable. */}
      <div
        className={`field-meta mt-1 leading-snug text-ink/80 ${comingSoon ? "select-none opacity-60 blur-[3px]" : ""}`}
      >
        {desc}
      </div>
      <div
        className={`field-label mt-auto pt-3 text-ink/80 ${comingSoon ? "select-none opacity-60 blur-[3px]" : ""}`}
      >
        {tags}
      </div>
    </>
  );
}
